import { NavigationGuard } from 'vue-router'
import { useSingleGroupMemento } from '@teams/common/hooks/useMemento'
import { initStore } from '@/store'
import { useUserStore } from '@/store/user'
import { useGroupStore } from '@/store/group'
import useTeamShare from '@teams/common/hooks/useTeamShare'
import useDw from '@teams/common/dw/useDw'
import { useError } from '@/views/error/useError'

const dw = useDw()
const { pageType } = useError()

type IRouterHandler = Record<
  string,
  (args: Parameters<NavigationGuard>) => Promise<void>
>

const userStore = useUserStore()

// 初始化空间备忘录 groupId不存在仅初始化副作用（切换团队需要）
export const initSingleGroupMemento = async (groupId?: string) => {
  const singleGroupMemento = useSingleGroupMemento()
  try {
    // 都需要初始化userStore (空间列表需要)
    await userStore.getCurrentAccount()
    // 都需要初始化workSpace信息
    await userStore.getCurrentWorkspace()
    if (groupId) {
      await singleGroupMemento.originator.changeState(() => ({
        groupId: groupId,
        userId: userStore.userId as number,
        shouldFlushStore: true,
        shouldFlushUsePreference: true,
        shouldFlushUseTeamShare: true
      }))
      singleGroupMemento.careTaker.backup()
    }
  } catch (error) {}
  singleGroupMemento.addAutoHandleSideEffectListener()
}

export const routerHandler: IRouterHandler = {
  Index: async args => {
    const [, , next] = args
    try {
      await initSingleGroupMemento()
    } catch (error) {}
    dw.dwSpaceShow({
      position: 'initial'
    })
    return next()
  },
  NotFound: async args => {
    // groupId校验异常(非纯数字) => 不需要请求空间信息
    const [, , next] = args
    try {
      await initSingleGroupMemento()
    } catch (error) {}
    dw.dwSpaceShow({
      position: pageType.value
    })
    return next()
  },
  Nav: async args => {
    const [to, , next] = args
    const groupStore = useGroupStore()
    const groupId = to.params.groupId as string
    try {
      await initSingleGroupMemento(groupId)
      // 检测空间是否存在
      await groupStore
        .fetchGroupInfo({
          throwError: true,
          groupId: Number(to.params.groupId)
        })
        .then(async () => {
          await initStore(groupId)
        })
    } catch (error) {
      // 异常页埋点
      dw.dwSpaceShow({
        position: pageType.value
      })
    }
    return next()
  },
  // 进入分享链接
  Join: async args => {
    const [to, , next] = args
    const groupStore = useGroupStore()
    const inviteKey = to.params.key as string
    try {
      // 从邀请链接进入
      groupStore.setIsFromInviteUrl(true)
      // 通过key拿邀请信息
      await groupStore.fetchInviteInfo(inviteKey)
      const { inviteInfo } = useTeamShare()
      const groupId = inviteInfo.value.invite_link?.group_id
      if (groupId) {
        await initSingleGroupMemento(groupId.toString())
        // 检测空间是否存在
        await groupStore.fetchGroupInfo({
          throwError: true,
          groupId
        })
        await initStore(groupId)
        return next({ name: 'Home', params: { groupId: groupId } })
      }
    } catch (error: any) {
      const { inviteInfo } = useTeamShare()
      const groupId = inviteInfo.value.invite_link?.group_id
      // 拿到groupId 跳转到https://k.wps.cn/xxx
      if (groupId) {
        dw.dwSpaceShow({
          position: pageType.value
        })
        return next({ name: 'Home', params: { groupId: groupId } })
      } else {
        // 没拿到groupId 初始化空间备忘录
        await initSingleGroupMemento()
        dw.dwSpaceShow({
          position: pageType.value
        })
      }
    }
    return next()
  },
  JumpLink: async args => {
    const [, , next] = args
    return next()
  }
}
